import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
// import { PrivateDocumentCategory } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  BrokerageAccountDetails,
  BrokerageAccountGeneral,
} from '../../sj-orm/models/assets/categories';
import {
  BlockingReasonType,
  BrokerageAccountType,
  KeyAssetsType,
} from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type BrokerageAccount = BrokerageAccountFormGeneralType &
  BrokerageAccountFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const BrokerageAccountList = [
  {
    label: 'forms_assets:brokerageAccount.enums.type.primary',
    key: BrokerageAccountType.primary,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.type.otc',
    key: BrokerageAccountType.otc,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.type.fixed',
    key: BrokerageAccountType.fixed,
  },
];

const KeyAssetsList = [
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.structureProduct',
    key: KeyAssetsType.structureProduct,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.substituteBonds',
    key: KeyAssetsType.substituteBonds,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.eurobonds',
    key: KeyAssetsType.eurobonds,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.corporateBonds',
    key: KeyAssetsType.corporateBonds,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.federalLoanBonds',
    key: KeyAssetsType.federalLoanBonds,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.bonds',
    key: KeyAssetsType.bonds,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.commonShares',
    key: KeyAssetsType.commonShares,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.preferredShares',
    key: KeyAssetsType.preferredShares,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.investmentFunds',
    key: KeyAssetsType.investmentFunds,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.options',
    key: KeyAssetsType.options,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.futures',
    key: KeyAssetsType.futures,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.currencyPairs',
    key: KeyAssetsType.currencyPairs,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.keyAssets.other',
    key: KeyAssetsType.other,
  },
];

const BlockingReasonList = [
  {
    label: 'forms_assets:brokerageAccount.enums.blockingReason.sanctions',
    key: BlockingReasonType.sanctions,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.blockingReason.unsignedOrders',
    key: BlockingReasonType.unsignedOrders,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.blockingReason.lackOfFundsForProvision',
    key: BlockingReasonType.lackOfFundsForProvision,
  },
  {
    label: 'forms_assets:brokerageAccount.enums.blockingReason.other',
    key: BlockingReasonType.other,
  },
];

type BrokerageAccountHelpGeneral = {
  nickName: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  keyAssets: QuestionType.SELECT_FROM_LIST;
  otherKeyAssets?: QuestionType.TEXT;
  holder: QuestionType.PICK_ARRAY_OF_DTO;
};

type BrokerageAccountHelpDetails = {
  plannedProfitability?: QuestionType.CURRENCY_CALCULATOR;
  actualProfitability?: QuestionType.CURRENCY_CALCULATOR;
  commissions?: QuestionType.NUMBER;
  totalExpenses?: QuestionType.NUMBER;
  tradingPlatforms?: QuestionType.TEXT;
  cif?: QuestionType.TEXT;
  iban?: QuestionType.TEXT;
  openingDate?: QuestionType.DATE_TIME;
  marginPosition?: QuestionType.CHECKBOX;
  marginPositionAvailableLimit?: QuestionType.TEXT;
  blocking?: QuestionType.CHECKBOX;
  blockingReason: QuestionType.SELECT_FROM_LIST;
  otherBlockingReason?: QuestionType.TEXT;
  strategyDescription?: QuestionType.TEXT;
  documents?: QuestionType.PICK_ARRAY_OF_DTO;
  attachments?: QuestionType.FILE_UPLOAD;
};

type BrokerageAccountFormGeneralType = TDtoWrapper<
  BrokerageAccountGeneral,
  BrokerageAccountHelpGeneral
>;

type BrokerageAccountFormDetailsType = TDtoWrapper<
  BrokerageAccountDetails,
  BrokerageAccountHelpDetails
>;

const BrokerageAccountFormGeneral: BrokerageAccountFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:brokerageAccount.inputs.type.label',
    value: BrokerageAccountType.primary,
    items: BrokerageAccountList,
    required: true,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:brokerageAccount.inputs.investedAssets.label',
    tooltip: 'forms_assets:brokerageAccount.inputs.investedAssets.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  keyAssets: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:brokerageAccount.inputs.keyAssets.label',
    tooltip: 'forms_assets:brokerageAccount.inputs.keyAssets.tooltip',
    value: KeyAssetsType.bonds,
    items: KeyAssetsList,
    mode: 'multiple',
  },
  otherKeyAssets: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.otherKeyAssets.label',
    value: '',
    showIf: {
      keyAssets: {
        value: KeyAssetsType.other,
      },
    },
  },
  holder: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:brokerageAccount.inputs.holder.label',
    value: [],
    colName: CollectionName.CONTACTS,
    yupScheme: yup.string().required('forms_assets:bankCells.inputs.holder.errors.required'),
    filter: (contact) =>
      [ContactType.BROKER, ContactType.BANK].includes((contact as unknown as ContactDto).type),
    dtoType: [ContactType.BANK, ContactType.BROKER],
    tooltip:
      'To add any broker to the list, please fill it out in the Contact section and you can choose it from the drop-down',
  },
};

const BrokerageAccountFormDetails: BrokerageAccountFormDetailsType = {
  tradingPlatforms: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.tradingPlatforms.label',
    tooltip: 'forms_assets:brokerageAccount.inputs.tradingPlatforms.tooltip',
    order: 2,
  },
  cif: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.cif.label',
    order: 4,
  },
  iban: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.iban.label',
    order: 5,
  },
  openingDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:brokerageAccount.inputs.openingDate.label',
    order: 6,
  },
  marginPosition: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:brokerageAccount.inputs.marginPosition.label',
    tooltip: 'forms_assets:brokerageAccount.inputs.marginPosition.tooltip',
    order: 7,
  },
  marginPositionAvailableLimit: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.marginPositionAvailableLimit.label',
    showIf: {
      marginPosition: {
        value: true,
      },
    },
    order: 8,
  },
  blocking: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:brokerageAccount.inputs.blocking.label',
    order: 9,
  },
  blockingReason: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:brokerageAccount.inputs.blockingReason.label',
    items: BlockingReasonList,
    showIf: {
      blocking: {
        value: true,
      },
    },
    order: 10,
  },
  otherBlockingReason: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.otherBlockingReason.label',
    showIf: {
      blockingReason: {
        value: BlockingReasonType.other,
      },
    },
    order: 11,
  },
  strategyDescription: {
    type: QuestionType.TEXT,
    label: 'forms_assets:brokerageAccount.inputs.strategyDescription.label',
    order: 12,
  },
  attachments: baseAssetFields.attachments,
  // insuranceAgreement: baseAassetFields.insuranceAgreement,
};

const BrokerageAccountForm = [
  BrokerageAccountFormGeneral,
  BrokerageAccountFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder(['availableLimit', 'expenses']),
  assetsStatisticsBuilder([
    'taxAgent',
    'taxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitability',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
  ]).form,
  assetsDocumentsBuilder(),
];

export { BrokerageAccountForm };
